import { StorySlug } from "@/api/episodes-types";
import { PromptType } from "@/api/prompts-types";
import { AlertSnackbar, ApiErrorSnackbar } from "@/components/ui/Snackbar";
import { useCreatePrompt } from "@/queries/prompts/use-create-prompt";
import { pretty, prettyStoryType } from "@/utils/pretty";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";

export function CreatePrompt() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <CreatePromptButton onClick={() => setOpen(true)} />
      <CreatePromptDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function CreatePromptButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant={"contained"} onClick={onClick}>
      New prompt
    </Button>
  );
}

function CreatePromptDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  const {
    mutate: createPrompt,
    error,
    isPending,
    isSuccess,
  } = useCreatePrompt();

  // undefined means input not touched
  const [name, setName] = useState<string>();
  const [storyType, setStoryType] = useState<StorySlug>();
  const [type, setType] = useState<PromptType>();
  const [description, setDescription] = useState<string>();
  const [prompt, setPrompt] = useState<string>();
  const [revisionNote, setRevisionNote] = useState<string>();

  const [nameError, setNameError] = useState<string>();
  const [storyTypeError, setStoryTypeError] = useState<string>();
  const [typeError, setTypeError] = useState<string>();
  const [promptError, setPromptError] = useState<string>();

  function handleClose() {
    setName(undefined);
    setStoryType(undefined);
    setType(undefined);
    setDescription(undefined);
    setPrompt(undefined);
    setRevisionNote(undefined);
    setNameError(undefined);
    setStoryTypeError(undefined);
    setTypeError(undefined);
    setPromptError(undefined);
    onClose();
  }

  function handleCreate() {
    if (!name || !storyType || !type || !prompt) {
      if (!name) setNameError("Name required");
      if (!storyType) setStoryTypeError("Story type required");
      if (!type) setTypeError("Prompt type required");
      if (!prompt) setPromptError("Prompt required");
      return;
    }
    createPrompt(
      { name, storyType, type, description, prompt, revisionNote },
      {
        onSuccess: (data) => {
          handleClose();
          navigate(`/prompts/${data.id}`);
        },
      },
    );
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Create New Prompt</DialogTitle>
        <DialogContent>
          <CreatePromptForm
            name={name ?? ""}
            nameError={nameError}
            storyType={storyType}
            storyTypeError={storyTypeError}
            type={type}
            typeError={typeError}
            description={description ?? ""}
            prompt={prompt ?? ""}
            promptError={promptError}
            revisionNote={revisionNote ?? ""}
            onNameChange={(value) => {
              setName(value);
              setNameError(undefined);
            }}
            onStoryTypeChange={(value) => {
              setStoryType(value);
              setStoryTypeError(undefined);
            }}
            onTypeChange={(value) => {
              setType(value);
              setTypeError(undefined);
            }}
            onDescriptionChange={(value) => setDescription(value)}
            onRevisionNoteChange={(value) => setRevisionNote(value)}
            onPromptChange={(value) => {
              setPrompt(value);
              setPromptError(undefined);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            loading={isPending}
            variant={"contained"}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <ApiErrorSnackbar error={error} />
      <AlertSnackbar severity="success">
        {isSuccess ? "Prompt created successfully" : undefined}
      </AlertSnackbar>
    </>
  );
}

function CreatePromptForm({
  name,
  nameError,
  storyType,
  storyTypeError,
  type,
  typeError,
  description,
  prompt,
  promptError,
  revisionNote,
  onNameChange,
  onStoryTypeChange,
  onTypeChange,
  onDescriptionChange,
  onPromptChange,
  onRevisionNoteChange,
}: {
  name: string;
  nameError?: string;
  storyType: StorySlug | undefined;
  storyTypeError?: string;
  type: PromptType | undefined;
  typeError?: string;
  description: string;
  prompt: string;
  promptError?: string;
  revisionNote: string;
  onNameChange: (name: string) => void;
  onStoryTypeChange: (storyType: StorySlug) => void;
  onTypeChange: (type: PromptType) => void;
  onDescriptionChange: (description: string) => void;
  onPromptChange: (prompt: string) => void;
  onRevisionNoteChange: (revisionNote: string) => void;
}) {
  return (
    <form>
      <Stack gap={2} sx={{ mt: 0.75 }}>
        <TextField
          label="Name"
          id="create-prompt-form_name"
          type="text"
          error={!!nameError}
          helperText={nameError}
          value={name}
          onChange={(e) => {
            onNameChange(e.target.value);
          }}
        />
        <TextField
          label="Story type"
          id="create-prompt-form_story-type"
          select
          error={!!storyTypeError}
          helperText={storyTypeError}
          value={storyType ?? ""}
          onChange={(e) => {
            onStoryTypeChange(e.target.value as StorySlug);
          }}
        >
          {Object.values(StorySlug).map((slug) => (
            <MenuItem key={slug} value={slug}>
              {prettyStoryType(slug)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Prompt type"
          id="create-prompt-form_prompt-type"
          select
          error={!!typeError}
          helperText={typeError}
          value={type ?? ""}
          onChange={(e) => {
            onTypeChange(e.target.value as PromptType);
          }}
        >
          {Object.values(PromptType).map((slug) => (
            <MenuItem key={slug} value={slug}>
              {pretty(slug)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Description"
          id="create-prompt-form_description"
          type="text"
          multiline={true}
          minRows={3}
          value={description}
          onChange={(e) => {
            onDescriptionChange(e.target.value);
          }}
        />
        <TextField
          label="Revision note"
          id="create-prompt-form_revision-note"
          type="text"
          value={revisionNote}
          onChange={(e) => {
            onRevisionNoteChange(e.target.value);
          }}
        />
        <TextField
          label="Prompt"
          multiline={true}
          id="create-prompt-form_prompt"
          error={!!promptError}
          helperText={promptError}
          value={prompt}
          minRows={15}
          onChange={(e) => {
            onPromptChange(e.target.value);
          }}
        />
      </Stack>
    </form>
  );
}
